/**
 * Hatch entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../hatchToRenderable.ts`. Reads the
 * canonical `HatchEntity` fields directly off the model (`model.ts`:
 * `hatchEntitySchema` — `patternName` / `patternScale` / `patternAngle` /
 * `solidFill` / `elevation` / `boundaryPaths` / `gradient`) instead of the
 * legacy `EntityPreview.properties` string bag. Produces a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * # Golden-parity quirks reproduced EXACTLY (NOT "better")
 *
 *   - Boundary paths: `entity.boundaryPaths` is passed STRAIGHT to the shared
 *     builder. The legacy path JSON.stringify → JSON.parse round-trips the SAME
 *     `boundaryPaths` (plain data: points / numbers / strings / booleans survive
 *     byte-identically), so the typed direct read is identical input. Both feed
 *     the SAME `walkHatchBoundary` + scene emitter → vertex-identical loops,
 *     including the `start == end` arc / ellipticArc → FULL CIRCLE guard.
 *
 *   - Pattern name: `patternName` schema-`.default("ANSI31")`; the producer
 *     writes `entity.patternName ?? "ANSI31"` and the legacy adapter reads
 *     `String(props.Pattern ?? "ANSI31")`. Passed through; the builder forces it
 *     to "SOLID" when `solid`.
 *
 *   - Pattern scale: `patternScale` schema-`.default(1)`. Legacy uses plain
 *     `readNumber("Scale", 1)` (finite-or-default), NOT `readPositiveNumber`, so
 *     a 0 / negative model value PASSES THROUGH unchanged — do NOT `positiveOr`
 *     clamp it. Passed verbatim.
 *
 *   - Pattern angle: `patternAngle` is in DEGREES (the producer writes it
 *     verbatim; the legacy adapter reads degrees then multiplies `* π/180`
 *     once). Passed in degrees; the shared builder does the SINGLE deg → rad
 *     conversion — NOT a double-convert.
 *
 *   - Solid: `solid = entity.solidFill === true || patternName.toUpperCase() ===
 *     "SOLID"` — the `patternName === "SOLID"` OR-clause is preserved (the legacy
 *     `Solid: "Yes"` → true is the `solidFill` boolean; the OR-clause forces
 *     solid even when `solidFill` is false).
 *
 *   - Elevation: `elevation` schema-`.default(0)`. Legacy plain
 *     `readNumber("Elevation", 0)`. Passed verbatim (used as the per-vertex Z
 *     lift inside the builder's emitter).
 *
 *   - Gradient: `entity.gradient` (typed `HatchGradient`) is resolved into the
 *     SAME `SceneGradient` the legacy `__hatch_gradient` channel carries, via the
 *     shared `resolveTypedSceneGradient` (mirrors `dxfGeometry.resolveSceneGradient`
 *     field-for-field). OMITTED (→ `undefined`) when `gradient` is absent OR
 *     `kind !== 1` — mirroring BOTH producer conditions.
 *
 * Category B: standalone file; NOT imported into any render path yet (the
 * dispatcher Hatch case is wired in the later Wire phase).
 */

import type { HatchEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity } from "../../types";

import { buildRenderableShell, type AdapterContext } from "../adapterContext";
import {
  buildHatchSceneGeometry,
  resolveTypedSceneGradient,
} from "../geom/hatchGeometry";
import { resolveTypedAnnotation } from "./annotativeResolve";

/**
 * Typed adapter for the `Hatch` entity (`hatchEntitySchema`).
 *
 * S10d — only the pattern fill scales/rotates per annotation scale; the
 * boundary loop stays in world coordinates (AutoCAD `AcDbHatchScaleContextData`
 * semantics), so a matched representation overrides `patternScale` /
 * `patternAngle` only. VISIBILITY (hiding an annotative HATCH with no
 * representation at the active scale) is gated in `renderableFromDrawingEntity`,
 * so this adapter keeps its non-null return.
 */
export function hatchFromEntity(
  entity: HatchEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // S10d — resolve the active-scale annotative representation (single truth).
  // No-op for non-annotative HATCH / no active scale.
  const anno = resolveTypedAnnotation(entity.annotativeContexts, ctx);
  // A matched HATCH context (`kind: "hatch"`) surfaces the ABSOLUTE per-scale
  // pattern scale as `effectiveScale` and the per-scale pattern angle as
  // `rotation` (see `resolveByContextKind`'s hatch branch). `activeContext` is
  // null for the non-annotative / no-match case → master fields kept.
  const annoHatch = anno.activeContext?.kind === "hatch";

  // `patternName` is schema-`.default("ANSI31")`; the `?? "ANSI31"` mirrors the
  // legacy `String(props.Pattern ?? "ANSI31")` (harmless given the default).
  const patternName = entity.patternName ?? "ANSI31";
  // Solid: the boolean model field OR the legacy `patternName === "SOLID"`
  // OR-clause (MUST preserve — forces solid even when `solidFill` is false).
  const solid = entity.solidFill === true || patternName.toUpperCase() === "SOLID";

  // The typed-side gradient resolution is the shared one-truth in
  // ../geom/hatchGeometry.ts (`resolveTypedSceneGradient`); it mirrors
  // `dxfGeometry.resolveSceneGradient` and OMITs (→ undefined) for a
  // non-gradient hatch (`gradient` absent or `kind !== 1`).
  const gradient = resolveTypedSceneGradient(entity.gradient);

  const { geometry, bounds } = buildHatchSceneGeometry({
    // `boundaryPaths` is the SAME plain-data shape the legacy path round-trips
    // through JSON — passed straight in, no conversion.
    boundaryPaths: entity.boundaryPaths,
    patternName,
    // Plain finite-or-default (legacy `readNumber`, NOT positive-clamped) — a
    // 0 / negative model value passes through unchanged. Default 1. S10d — a
    // matched annotative representation overrides it with the per-scale pattern
    // scale (`effectiveScale` = the absolute per-scale patternScale).
    patternScale: annoHatch ? anno.effectiveScale : entity.patternScale ?? 1,
    // In DEGREES — the builder does the single deg → rad. Default 0. S10d — a
    // matched representation overrides it with the per-scale pattern angle.
    patternAngleDeg: annoHatch ? anno.rotation ?? 0 : entity.patternAngle ?? 0,
    solid,
    elevation: entity.elevation ?? 0,
    gradient,
  });

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
